import styled from "@emotion/styled"
import { colorCanvas, colorContrastLow, radiusMd, shadowSm } from "@porsche-design-system/components-react/emotion"
import type { PropsWithChildren } from "react"
import { useMemo } from "react"
import { createPortal } from "react-dom"
import { useIntl } from "react-intl"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { useDirection } from "src/context/translations/useDirection"
import type { HoverableFlyoutProps } from "./HoverableFlyout"

interface FlyoutPortalBounds {
	bottom?: number
	left: number
	top?: number
	width: number
}

interface FlyoutPortalProps extends PropsWithChildren {
	align: HoverableFlyoutProps["align"]
	direction: "up" | "down"
	fixed: HoverableFlyoutProps["fixed"]
	onMouseEnter?: () => void
	onMouseLeave?: () => void
	triggerBounds: DOMRect
	width: HoverableFlyoutProps["width"]
}

export const FlyoutPortal = (props: FlyoutPortalProps) => {
	const { align, children, direction, fixed, onMouseEnter, onMouseLeave, triggerBounds, width } = props
	const { locale } = useIntl()
	const dir = useDirection(locale)
	const isRTL = useMemo(() => dir === "rtl", [dir])

	// wider version to not have line break on long entries like "Print window sticker (Monroney Label) (NEW)"
	const flyoutWidth = width === "wide" ? 400 : width === "narrow" ? 250 : 325

	const bounds: FlyoutPortalBounds = {
		left: isRTL
			? align !== "right"
				? triggerBounds.right - flyoutWidth
				: triggerBounds.left
			: align !== "right"
				? triggerBounds.left
				: triggerBounds.right - flyoutWidth,
		width: flyoutWidth,
		...(direction === "up"
			? { bottom: window.innerHeight - window.scrollY - triggerBounds.top }
			: { top: triggerBounds.bottom + (fixed ? 0 : window.scrollY) }),
	}

	return createPortal(
		// eslint-disable-next-line no-restricted-syntax
		<Flyout $fixed={fixed} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} style={bounds}>
			{children}
		</Flyout>,
		document.body,
	)
}

const Flyout = styled("div", { shouldForwardProp })<{ $fixed: FlyoutPortalProps["fixed"] }>`
	position: ${({ $fixed }) => ($fixed ? "fixed" : "absolute")};
	border: 1px solid ${colorContrastLow};
	border-radius: ${radiusMd};
	z-index: 510;
	background: ${colorCanvas};
	box-shadow: ${shadowSm};
	overflow-y: auto;
	max-height: 700px;
	-webkit-overflow-scrolling: touch;
`
